import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, FileStack, ChevronRight } from 'lucide-react';
import { api, ApiClientError } from '../../lib/api.js';
import { TextField, TextAreaField } from '../../components/ui/Field.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { AccountSection } from './AccountSection.jsx';
import { PrintersSection } from './PrintersSection.jsx';
import { DriveSection } from './DriveSection.jsx';
import { EinvoiceSection } from './EinvoiceSection.jsx';
import { BackupsSection } from './BackupsSection.jsx';

const EMPTY = {
  shopName: '',
  legalName: '',
  gstin: '',
  pan: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  state: '',
  stateCode: '33',
  pincode: '',
  phone: '',
  altPhone: '',
  email: '',
  bankName: '',
  bankAccountNo: '',
  bankIfsc: '',
  bankBranch: '',
  upiId: '',
  declarationText: '',
  termsText: '',
};

function toForm(settings) {
  const form = { ...EMPTY };
  for (const key of Object.keys(EMPTY)) {
    const snakeKey = key.replace(/[A-Z]/g, (m) => `_${m.toLowerCase()}`);
    if (settings?.[snakeKey] != null) form[key] = settings[snakeKey];
  }
  return form;
}

export default function SettingsPage() {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['shop-settings'], queryFn: () => api.get('/shop-settings') });
  const [form, setForm] = useState(EMPTY);
  const [fieldErrors, setFieldErrors] = useState({});
  const [formError, setFormError] = useState(null);
  const [saved, setSaved] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [driveMessage, setDriveMessage] = useState(null);

  useEffect(() => {
    if (data) setForm(toForm(data));
  }, [data]);

  useEffect(() => {
    const driveResult = searchParams.get('drive');
    if (!driveResult) return;
    setDriveMessage(driveResult);
    queryClient.invalidateQueries({ queryKey: ['drive', 'status'] });
    const next = new URLSearchParams(searchParams);
    next.delete('drive');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveMutation = useMutation({
    mutationFn: () => api.put('/shop-settings', form),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shop-settings'] });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    },
    onError: (err) => {
      if (err instanceof ApiClientError && err.details) setFieldErrors(err.details);
      else setFormError(err instanceof ApiClientError ? err.message : 'Could not save. Please try again.');
    },
  });

  const onSubmit = (e) => {
    e.preventDefault();
    setFieldErrors({});
    setFormError(null);
    saveMutation.mutate();
  };

  const setField = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  if (isLoading) return null;

  return (
    <div className="flex flex-col gap-5">
      <h1 className="font-display text-2xl font-semibold tracking-tight">Shop settings</h1>

      {driveMessage === 'connected' ? (
        <p className="flex items-center gap-1.5 rounded-card border border-success/30 bg-success/10 px-3.5 py-2.5 text-sm text-success">
          <CheckCircle2 size={16} aria-hidden="true" /> Google Drive connected.
        </p>
      ) : driveMessage === 'error' ? (
        <p className="rounded-card border border-danger/30 bg-danger/10 px-3.5 py-2.5 text-sm text-danger">
          Could not connect Google Drive. Please try again.
        </p>
      ) : null}
      <p className="-mt-3 max-w-2xl text-sm text-muted">
        Printed on every invoice and voucher. The GSTIN and state decide whether a sale is billed CGST + SGST or IGST.
      </p>

      <form onSubmit={onSubmit} className="card flex max-w-2xl flex-col gap-6 p-6">
        <section className="flex flex-col gap-4">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-muted">Identity</h2>
          <TextField label="Shop name" required value={form.shopName} onChange={setField('shopName')} error={fieldErrors.shopName} />
          <TextField label="Legal / registered name" value={form.legalName} onChange={setField('legalName')} />
          <div className="grid grid-cols-2 gap-3">
            <TextField label="GSTIN" value={form.gstin} onChange={setField('gstin')} error={fieldErrors.gstin} />
            <TextField label="PAN" value={form.pan} onChange={setField('pan')} />
          </div>
        </section>

        <section className="flex flex-col gap-4 border-t border-border pt-5">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-muted">Address</h2>
          <TextField label="Address line 1" value={form.addressLine1} onChange={setField('addressLine1')} />
          <TextField label="Address line 2" value={form.addressLine2} onChange={setField('addressLine2')} />
          <div className="grid grid-cols-3 gap-3">
            <TextField label="City" value={form.city} onChange={setField('city')} />
            <TextField label="State" value={form.state} onChange={setField('state')} />
            <TextField
              label="State code"
              required
              value={form.stateCode}
              onChange={setField('stateCode')}
              error={fieldErrors.stateCode}
              hint="e.g. 33 for Tamil Nadu"
            />
          </div>
          <TextField label="PIN code" value={form.pincode} onChange={setField('pincode')} error={fieldErrors.pincode} />
        </section>

        <section className="flex flex-col gap-4 border-t border-border pt-5">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-muted">Contact</h2>
          <div className="grid grid-cols-2 gap-3">
            <TextField label="Phone" value={form.phone} onChange={setField('phone')} />
            <TextField label="Alternate phone" value={form.altPhone} onChange={setField('altPhone')} />
          </div>
          <TextField label="Email" value={form.email} onChange={setField('email')} error={fieldErrors.email} />
        </section>

        <section className="flex flex-col gap-4 border-t border-border pt-5">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-muted">Bank &amp; UPI</h2>
          <TextField label="Bank name" value={form.bankName} onChange={setField('bankName')} />
          <div className="grid grid-cols-2 gap-3">
            <TextField label="Account number" value={form.bankAccountNo} onChange={setField('bankAccountNo')} />
            <TextField label="IFSC" value={form.bankIfsc} onChange={setField('bankIfsc')} />
          </div>
          <TextField label="Branch" value={form.bankBranch} onChange={setField('bankBranch')} />
          <TextField label="UPI ID" value={form.upiId} onChange={setField('upiId')} />
        </section>

        <section className="flex flex-col gap-4 border-t border-border pt-5">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-muted">Invoice text</h2>
          <TextAreaField label="Declaration" value={form.declarationText} onChange={setField('declarationText')} />
          <TextAreaField label="Terms" value={form.termsText} onChange={setField('termsText')} />
        </section>

        {formError ? (
          <p role="alert" className="rounded-card border border-danger/30 bg-danger/10 px-3 py-2 text-sm text-danger">
            {formError}
          </p>
        ) : null}

        <div className="flex items-center gap-3">
          <Button type="submit" loading={saveMutation.isPending}>
            Save changes
          </Button>
          {saved ? (
            <span className="flex items-center gap-1.5 text-sm text-success">
              <CheckCircle2 size={16} aria-hidden="true" /> Saved
            </span>
          ) : null}
        </div>
      </form>

      <div className="card flex max-w-2xl flex-col gap-6 p-6">
        <AccountSection />
      </div>

      <div className="card flex max-w-2xl flex-col gap-6 p-6">
        <PrintersSection />
      </div>

      <div className="card flex max-w-2xl flex-col gap-6 p-6">
        <DriveSection />
      </div>

      <div className="card flex max-w-2xl flex-col gap-6 p-6">
        <EinvoiceSection />
      </div>

      <div className="card flex max-w-2xl flex-col gap-6 p-6">
        <BackupsSection />
      </div>

      <Link
        to="/settings/templates"
        className="card flex max-w-2xl items-center gap-3 p-6 hover:bg-surface-2"
      >
        <FileStack size={20} className="text-muted" aria-hidden="true" />
        <div className="flex-1">
          <h2 className="text-sm font-semibold">Document templates</h2>
          <p className="text-sm text-muted">
            Upload a Canva-designed voucher and map its blanks — the visual field mapper needs its own screen.
          </p>
        </div>
        <ChevronRight size={18} className="text-muted" aria-hidden="true" />
      </Link>
    </div>
  );
}
